// supervisor-halt-directive-step.ts
//
// Phase 04 (R-01, TD5): tick-boundary step consuming durable pause/resume/
// abort/abort_confirm directives. Pause/resume handling itself moved to
// supervisor-pause-resume-directive-step.ts (D12, phase 05 B1 — this file
// sat at the 200-line ceiling); this file still dispatches to it.
//
// Order per pass: ABORTED short-circuits first, then interrupted, then
// pending directives sorted by `received_at` (tie-break by id). Confirm
// success: commit -> mark -> delete challenge (plan.md Risk Assessment).
import { randomUUID } from "node:crypto";
import {
  AbortConfirmRejectedError,
  confirmAbortChallenge,
  deleteAbortChallenge,
  issueAbortChallenge,
  readAbortChallenge,
} from "./abort-challenge.js";
import {
  isDirectiveProcessed,
  listInboxDirectiveIds,
  markDirectiveProcessed,
  readDirective,
} from "./directive-inbox.js";
import type { MissionStore } from "./mission-store.js";
import { readPauseResumeState } from "./pause-resume-state.js";
import type { Directive } from "./schemas/directive-schema.js";
// D12 (phase 05 B1): pause/resume handling split out — see that module's
// header for why (journals now, was pause-state.json only).
import { applyPauseOrResumeDirective } from "./supervisor-pause-resume-directive-step.js";
import type { RunUntilIdleInput } from "./supervisor-run-until-idle-types.js";

// Q3: no incrementing "revision" in mission.json — real binding is actor + one TTL window + used once.
export const ABORT_CHALLENGE_MISSION_REVISION = 1;

type Keys = "store" | "controlRoot" | "missionId" | "abortChallengeTtlSec" | "notify";
export type HaltDirectiveStepInput = Pick<RunUntilIdleInput, Keys | "isInterrupted">;

const HALT_KINDS = ["pause", "resume", "abort", "abort_confirm"] as const;

async function pendingHaltDirectiveList(controlRoot: string): Promise<Directive[]> {
  const ids = await listInboxDirectiveIds(controlRoot);
  const pending: Directive[] = [];
  for (const id of ids) {
    if (await isDirectiveProcessed(controlRoot, id)) continue;
    const directive = await readDirective(controlRoot, id);
    if (directive && (HALT_KINDS as readonly string[]).includes(directive.kind)) {
      pending.push(directive);
    }
  }
  return pending.sort(
    (a, b) =>
      a.received_at.localeCompare(b.received_at) || a.directive_id.localeCompare(b.directive_id),
  );
}

async function journalDelta(store: MissionStore, delta: Record<string, unknown>): Promise<void> {
  await store.commit([{ event_id: randomUUID(), delta: delta as never }]);
}

function journalRejected(store: MissionStore, directive: Directive, reason: string): Promise<void> {
  const { directive_id, kind: directive_kind } = directive;
  return journalDelta(store, { kind: "directive_rejected", directive_id, directive_kind, reason });
}

function journalAbortIssued(
  store: MissionStore,
  challengeId: string,
  actorId: string,
  source: string,
): Promise<void> {
  return journalDelta(store, {
    kind: "abort_challenge_issued",
    challenge_id: challengeId,
    actor_id: actorId,
    source,
  });
}

/** [B2] `abort` with a `challenge_id` already (CLI issued it in step 1) — journals the event, never mints a second challenge. */
export async function applyCliIssuedAbortDirective(input: {
  store: MissionStore;
  controlRoot: string;
  directive: Directive;
}): Promise<void> {
  const { store, controlRoot, directive } = input;
  const id = String((directive.payload as Record<string, unknown>).challenge_id ?? "");
  let challenge: Awaited<ReturnType<typeof readAbortChallenge>>;
  try {
    challenge = await readAbortChallenge(controlRoot, id);
  } catch (err) {
    if (!(err instanceof AbortConfirmRejectedError)) throw err;
    await journalRejected(store, directive, err.message);
    await markDirectiveProcessed(controlRoot, directive.directive_id);
    return;
  }
  if (!challenge || challenge.actor_id !== directive.actor_id) {
    const reason = !challenge
      ? `Challenge "${id}" không tồn tại.`
      : `Actor "${directive.actor_id}" khác actor đã khởi tạo challenge ("${challenge.actor_id}").`;
    await journalRejected(store, directive, reason);
    await markDirectiveProcessed(controlRoot, directive.directive_id);
    return;
  }
  await journalAbortIssued(store, id, challenge.actor_id, directive.source);
  await markDirectiveProcessed(controlRoot, directive.directive_id);
}

/** `abort` directive with NO `challenge_id` (legacy CLI or Mattermost) — mints a fresh challenge, journals it, and notifies the operator with the confirm command. Never dispatched for a CLI-issued abort (that one already carries an id — see applyCliIssuedAbortDirective). */
async function issueChallengeForAbortDirective(
  input: HaltDirectiveStepInput,
  directive: Directive,
): Promise<void> {
  const challenge = await issueAbortChallenge({
    controlRoot: input.controlRoot,
    missionId: input.missionId,
    actorId: directive.actor_id,
    missionRevision: ABORT_CHALLENGE_MISSION_REVISION,
    ttlSec: input.abortChallengeTtlSec ?? 300,
  });
  const { store, controlRoot } = input;
  await journalAbortIssued(store, challenge.challenge_id, challenge.actor_id, directive.source);
  input.notify?.(confirmHintLine(challenge));
  await markDirectiveProcessed(controlRoot, directive.directive_id);
}

function confirmHintLine(challenge: Awaited<ReturnType<typeof issueAbortChallenge>>): string {
  const { challenge_id, expires_at } = challenge;
  return `Abort challenge ${challenge_id} — hạn ${expires_at}; xác nhận: avatar autopilot abort --confirm ${challenge_id}`;
}

/** Step 2 authority check, shared by the in-loop halt-step and the startup queued-confirm pass ([B1] supervisor-mission-run-startup.ts) — both need the exact same commit -> mark -> delete ordering. */
export async function applyAbortConfirmDirective(input: {
  store: MissionStore;
  controlRoot: string;
  directive: Directive;
  notify?: (line: string) => void;
}): Promise<boolean> {
  const { store, controlRoot, directive, notify } = input;
  const id = String((directive.payload as Record<string, unknown>).challenge_id ?? "");
  try {
    await confirmAbortChallenge({
      controlRoot,
      challengeId: id,
      actorId: directive.actor_id,
      missionRevision: ABORT_CHALLENGE_MISSION_REVISION,
      now: () => new Date(directive.received_at),
    });
  } catch (err) {
    if (!(err instanceof AbortConfirmRejectedError)) throw err;
    await journalRejected(store, directive, err.message);
    notify?.(`Abort confirm ${directive.directive_id} bị từ chối: ${err.message}`);
    await markDirectiveProcessed(controlRoot, directive.directive_id);
    return false;
  }
  await journalDelta(store, { kind: "mission_status_changed", status: "ABORTED" });
  await markDirectiveProcessed(controlRoot, directive.directive_id);
  await deleteAbortChallenge(controlRoot, id);
  return true;
}

/** Tick-boundary halt check: `"aborted"` (permanently done), `"paused"` (stop dispatching, keep state), `"interrupted"` (Ctrl-C/SIGTERM, phase 05), or `undefined` (proceed this tick). */
export async function applyHaltDirectivesAtTickBoundary(
  input: HaltDirectiveStepInput,
): Promise<"paused" | "aborted" | "interrupted" | undefined> {
  if (input.store.state.status === "ABORTED") return "aborted";

  const sig = input.isInterrupted?.();
  if (sig) {
    const kind = "mission_status_changed";
    await journalDelta(input.store, { kind, status: "interrupted", signal: sig });
    return "interrupted";
  }

  for (const directive of await pendingHaltDirectiveList(input.controlRoot)) {
    if (directive.kind === "pause" || directive.kind === "resume") {
      await applyPauseOrResumeDirective(input.store, input.controlRoot, directive, input.notify);
    } else if (directive.kind === "abort") {
      const payload = directive.payload as Record<string, unknown>;
      if (payload.challenge_id !== undefined) {
        const { store, controlRoot } = input;
        await applyCliIssuedAbortDirective({ store, controlRoot, directive });
      } else {
        await issueChallengeForAbortDirective(input, directive);
      }
    } else if (directive.kind === "abort_confirm") {
      const aborted = await applyAbortConfirmDirective({
        store: input.store,
        controlRoot: input.controlRoot,
        directive,
        notify: input.notify,
      });
      if (aborted) return "aborted";
    }
  }

  return (await readPauseResumeState(input.controlRoot)).paused ? "paused" : undefined;
}
